/**
 * HTML-представление документов.
 *
 * Один и тот же HTML идёт и в превью браузера, и в headless Chromium для PDF.
 * Это осознанный выбор: альтернатива — рисовать PDF отдельной библиотекой
 * (`pdf-lib`), заново воспроизводя таблицу банковских реквизитов с её
 * вертикальными объединениями. Тогда превью и печать неизбежно разъезжаются,
 * и расхождение обнаруживает бухгалтер клиники, а не разработчик.
 *
 * Вёрстка намеренно простая: таблицы, границы, никакого flex/grid в теле
 * документа. Так она одинаково ведёт себя при печати на A4 и при вставке
 * в Word, если кто-то откроет HTML напрямую.
 */
/** Экранирование пользовательского текста: названия позиций вводит пользователь. */
export function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/**
 * Стили документа.
 *
 * Шрифт — Times New Roman: им набраны исходные документы лаборатории, и
 * при сверке с прежними счетами разница в гарнитуре бросается в глаза первой.
 * Размеры в пунктах, а не в пикселях, — документ предназначен для печати.
 */
/**
 * Стили документа, ограниченные заданным корнем.
 *
 * КАЖДОЕ правило начинается с корневого селектора, и это не педантизм.
 * Раньше набор подключался в окне предпросмотра как есть, а среди правил
 * были `body`, `table`, `td, th` и `p` — то есть, пока открыто окно счёта,
 * всё приложение за ним набиралось Times New Roman в 11 пунктов, а каждая
 * ячейка любой таблицы получала чёрную рамку. Увидеть это трудно: окно само
 * закрывает собой испорченное.
 *
 * Для самостоятельной страницы (PDF и скачивание) корень — `body`;
 * для предпросмотра внутри приложения — класс обёртки.
 */
function documentCss(root) {
    return `
  ${root} * { box-sizing: border-box; }
  ${root} {
    margin: 0;
    font-family: "Times New Roman", "Liberation Serif", Times, serif;
    /* 11 пт — кегль исходных документов лаборатории, не 10. */
    font-size: 11pt;
    line-height: 1.25;
    color: #000;
    background: #fff;
  }
  /* Поля страницы у счёта и акта РАЗНЫЕ — как в образцах. */
  ${root} .doc { padding: 10.6mm 15.9mm 25.4mm; }
  /* У нового эталона акта поля симметричные. */
  ${root} .doc-akt { padding: 20mm 15mm; }
  ${root} table { width: 100%; border-collapse: collapse; }
  ${root} td, ${root} th { border: 1px solid #000; padding: 3pt 5pt; vertical-align: top; text-align: left; }
  ${root} th { font-weight: normal; }
  ${root} .no-border td { border: none; padding: 0; }
  /*
    Обведены только платёжка и таблица позиций. У заголовка и блока итогов
    рамок нет, снизу остаётся тонкая линейка — по ней блок и отделяется.
  */
  ${root} .plain td { border: none; }
  ${root} .rule-below { border-bottom: 1.5pt solid #000; }
  ${root} .rule-below td { border: none; }
  ${root} .num { text-align: right; white-space: nowrap; }
  ${root} .center { text-align: center; }
  ${root} .bold { font-weight: bold; }
  ${root} .justify { text-align: justify; }
  ${root} .title { font-weight: bold; text-align: center; }
  ${root} .mb { margin-bottom: 8pt; }
  ${root} .mt { margin-top: 8pt; }
  ${root} .mt-lg { margin-top: 24pt; }
  ${root} p { margin: 0 0 4pt; }
  /* Акт набран РУБЛЕНОЙ гарнитурой — так в новом эталоне. */
  ${root} .doc-akt, ${root} .doc-akt table { font-family: Arial, 'Liberation Sans', Helvetica, sans-serif; font-size: 9pt; }
  ${root} .akt-title { font-size: 13pt; font-weight: bold; padding-bottom: 3pt; }
  ${root} .akt-label { vertical-align: middle; }
  ${root} .akt-parties td { padding: 0 5pt 0 0; }
  ${root} .akt-head { font-size: 11pt; font-weight: bold; }
  ${root} .akt-sub { text-align: center; margin-bottom: 10pt; }
  /* Пустая часть итоговой строки акта: ячейка есть, линий внутри нет. */
  ${root} .no-line { border-left: none; border-bottom: none; }
  ${root} .signatures { width: 100%; margin-top: 28pt; }
  ${root} .signatures td { border: none; padding: 0; width: 50%; vertical-align: top; }
  ${root} .sign-line { margin-top: 22pt; }
  /*
   * Факсимиле — поверх текста, а не в потоке: подпись пересекает черту,
   * печать заходит на подпись, как на бумаге. Ширины в долях строки, поэтому
   * в предпросмотре и на листе раскладка одна.
   */
  ${root} .sign-block { position: relative; }
  ${root} .fax-signature, ${root} .fax-stamp { position: absolute; height: auto; pointer-events: none; }
  ${root} .fax-stamp { mix-blend-mode: multiply; }
  /*
   * Печать центрирована на строке подписи, а не над ней: поднятая, она
   * наезжала на черту и оговорку. Ниже строки — место под её нижний край,
   * иначе предпросмотр обрезал печать.
   */
  ${root} .sign-block:has(.fax-schet) { padding-bottom: 64pt; }
  ${root} .signatures td:has(.fax-akt) { padding-bottom: 32pt; }
  ${root} .fax-signature.fax-schet { left: 34.6%; top: -30pt; width: 25.7%; }
  ${root} .fax-stamp.fax-schet { left: 59.4%; top: -28pt; width: 21.4%; }
  /* В акте печать — у конца черты: левее она закрывала должность и фамилию. */
  ${root} .fax-signature.fax-akt { left: 16%; top: -36pt; width: 42%; }
  ${root} .fax-stamp.fax-akt { left: 62%; top: -40pt; width: 36%; }
  @page { size: A4; margin: 0; }
  /* При печати поля берутся из самого блока — они разные у счёта и акта. */
`;
}
const DOCUMENT_CSS = documentCss('body');
/**
 * Таблица банковских реквизитов исполнителя — §2.5, пункт 1.
 *
 * Четыре колонки со сложными вертикальными объединениями. Раскладка перенесена
 * из XML реального файла: банк получателя занимает две строки слева, БИК и
 * корр. счёт — справа сверху, ИНН/КПП и расчётный счёт — ниже, и отдельной
 * строкой подпись «Получатель». Менять её «как логичнее» нельзя: это узнаваемая
 * форма банковской платёжки.
 */
function bankTable(model) {
    const b = model.bank;
    return `
  <table class="mb">
    <colgroup>
      <col style="width:24.2%"><col style="width:24.2%"><col style="width:9.7%"><col style="width:41.9%">
    </colgroup>
    <tbody>
      <tr>
        <td colspan="2" rowspan="2">${escapeHtml(b.bankName)}</td>
        <td>БИК</td>
        <td>${escapeHtml(b.bik)}</td>
      </tr>
      <tr>
        <td rowspan="2">Сч. №</td>
        <td rowspan="2">${escapeHtml(b.ks)}</td>
      </tr>
      <tr>
        <td colspan="2">Банк получателя</td>
      </tr>
      <tr>
        <td>ИНН ${escapeHtml(b.inn)}</td>
        <td>КПП ${escapeHtml(b.kpp)}</td>
        <td rowspan="4">Сч. №</td>
        <td rowspan="4">${escapeHtml(b.rs)}</td>
      </tr>
      <tr>
        <td colspan="2" rowspan="2">${escapeHtml(b.recipient)}</td>
      </tr>
      <tr></tr>
      <tr>
        <td colspan="2">Получатель</td>
      </tr>
    </tbody>
  </table>`;
}
/** Ширины из «Счет Стародубцева 336.docx», в долях от ширины таблицы. */
const ITEM_COLS = `
  <colgroup>
    <col style="width:4.9%"><col style="width:50.3%"><col style="width:9.8%">
    <col style="width:7.8%"><col style="width:13.6%"><col style="width:13.7%">
  </colgroup>`;
/**
 * Шапки таблицы позиций РАЗНЫЕ у счёта и акта.
 *
 * Счёт перечисляет «работы, услуги» — он выставляется до приёмки и покрывает
 * всё, что предъявлено к оплате. Акт перечисляет «услуги» — то, что уже
 * оказано. Различие взято из реальных документов и сохранено.
 */
function itemHead(kind) {
    const nameHeader = kind === 'schet' ? 'Наименование работ, услуг' : 'Наименование услуги';
    const unitHeader = kind === 'schet' ? 'Ед' : 'Ед.';
    // Шапка ЖИРНАЯ и ПО ЦЕНТРУ во всех колонках — как в образце.
    return `
  <thead>
    <tr>
      <th class="center bold">№</th>
      <th class="center bold">${nameHeader}</th>
      <th class="center bold">Кол-во</th>
      <th class="center bold">${unitHeader}</th>
      <th class="center bold">Цена</th>
      <th class="center bold">Сумма</th>
    </tr>
  </thead>`;
}
function itemRows(model) {
    return model.items
        .map((item) => `
      <!--
        Выключка из образца и на первый взгляд непоследовательна: номер, цена
        и сумма по центру, количество по правому краю. Так набрано
        в документах лаборатории.
      -->
      <tr>
        <td class="center">${item.index}</td>
        <td>${escapeHtml(item.name)}</td>
        <td class="num">${escapeHtml(item.qty)}</td>
        <td class="center">${escapeHtml(item.unit)}</td>
        <td class="center">${escapeHtml(item.price)}</td>
        <td class="center">${escapeHtml(item.total)}</td>
      </tr>`)
        .join('');
}
/** Подпись и печать исполнителя; пусто, если факсимиле в документе выключено. */
function facsimileImages(model) {
    if (!model.facsimile)
        return '';
    const place = model.kind === 'schet' ? 'fax-schet' : 'fax-akt';
    return (`<img class="fax-signature ${place}" src="${escapeHtml(model.facsimile.signature)}" alt="">` +
        `<img class="fax-stamp ${place}" src="${escapeHtml(model.facsimile.stamp)}" alt="">`);
}
/**
 * Тело счёта — §2.5.
 *
 * Итоги вынесены в ОТДЕЛЬНУЮ таблицу после позиций. Жирным набраны ПОДПИСИ
 * («Итого:», «Всего к оплате:»), а числа обычным — так в образце. Строка
 * «Сумма прописью» жирная целиком.
 */
function schetBody(model) {
    return `
  ${bankTable(model)}

  <table class="mb rule-below">
    <tbody>
      <tr><td class="title">${escapeHtml(model.title)}</td></tr>
    </tbody>
  </table>

  <!--
    Поставщик и покупатель — ПО ЛЕВОМУ КРАЮ, а не по ширине.

    В этих строках идут длинные «слова» без пробелов: ИНН/КПП через косую,
    «3808288498/380801001». Не влезая в строку, такое слово уходило целиком
    на следующую, и выравнивание по ширине растягивало оставшиеся четыре
    слова через всю страницу — «ООО      «Вектор      здоровья»,      ОГРН».
    По левому краю строка просто переносится, как в письме.
  -->
  <table class="mb plain">
    <colgroup><col style="width:14.8%"><col style="width:85.2%"></colgroup>
    <tbody>
      <tr>
        <td>Поставщик (Исполнитель):</td>
        <td class="bold">${escapeHtml(model.supplier)}</td>
      </tr>
      <tr>
        <td>Покупатель (Заказчик):</td>
        <td class="bold">${escapeHtml(model.customer)}</td>
      </tr>
      <tr>
        <td>Основание</td>
        <td class="bold">${escapeHtml(model.basis)}</td>
      </tr>
    </tbody>
  </table>

  <table>
    ${ITEM_COLS}
    ${itemHead(model.kind)}
    <tbody>${itemRows(model)}</tbody>
  </table>

  <!--
    Жирным набраны ПОДПИСИ итогов, а числа обычным — так в образце.
    Раньше было наоборот, и блок выглядел чужим бланком.
  -->
  <table class="rule-below">
    <colgroup><col style="width:86.3%"><col style="width:13.7%"></colgroup>
    <tbody>
      <tr>
        <td class="num bold">Итого:</td>
        <td class="num">${escapeHtml(model.total)}</td>
      </tr>
      <tr>
        <td class="num bold">Без налога НДС:</td>
        <td></td>
      </tr>
      <tr>
        <td class="num bold">Всего к оплате:</td>
        <td class="num">${escapeHtml(model.total)}</td>
      </tr>
      <tr>
        <td colspan="2">Всего наименований ${model.itemCount}, на сумму <span class="bold">${escapeHtml(model.total)} руб.</span></td>
      </tr>
      <tr>
        <td colspan="2">
          <p class="bold">Сумма прописью: ${escapeHtml(model.totalInWords)}</p>
          <p>${escapeHtml(model.disclaimer)}</p>
        </td>
      </tr>
    </tbody>
  </table>

  <p class="mt-lg sign-block"><b>Предприниматель</b> __________________________________________________________<b>${escapeHtml(model.performerSigner)}</b>${facsimileImages(model)}</p>`;
}
/**
 * Тело акта — по НОВОМУ эталону.
 *
 * Отличия от счёта не косметические, поэтому и разметка своя:
 * рубленая гарнитура, заголовок одной строкой слева, стороны тремя строками,
 * итог ВЫНЕСЕН из таблицы, суммы «19 500,00», подписи с должностью.
 */
function aktBody(model) {
    const party = (label, value) => `
      <tr>
        <td class="akt-label">${escapeHtml(label)}</td>
        <td class="bold">${escapeHtml(value)}</td>
      </tr>`;
    return `
  <p class="akt-title rule-below">${escapeHtml(model.title)}</p>

  <table class="akt-parties plain mt">
    <colgroup><col style="width:12.4%"><col style="width:87.6%"></colgroup>
    <tbody>
      ${party('Исполнитель:', `${model.performerName} ${model.performerDetails}`)}
      <tr><td>&nbsp;</td><td>&nbsp;</td></tr>
      ${party('Заказчик:', model.customerFull)}
      <tr><td>&nbsp;</td><td>&nbsp;</td></tr>
      ${party('Основание:', model.contractReference)}
    </tbody>
  </table>

  <table class="mt">
    <colgroup>
      <col style="width:6.1%"><col style="width:51.7%"><col style="width:9.0%">
      <col style="width:7.4%"><col style="width:11.8%"><col style="width:14.0%">
    </colgroup>
    <thead>
      <tr>
        <th class="center bold">№</th>
        <th class="center bold">Наименование работ, услуг</th>
        <th class="center bold">Кол&#8209;во</th>
        <th class="center bold">Ед.</th>
        <th class="center bold">Цена</th>
        <th class="center bold">Сумма</th>
      </tr>
    </thead>
    <tbody>
      ${model.items
        .map((item) => `
      <tr>
        <td class="center">${item.index}</td>
        <td>${escapeHtml(item.name)}</td>
        <td class="center">${escapeHtml(item.qty)}</td>
        <td class="center">${escapeHtml(item.unit)}</td>
        <td class="num">${escapeHtml(item.price)}</td>
        <td class="num">${escapeHtml(item.total)}</td>
      </tr>`)
        .join('')}
    </tbody>
  </table>

  <!--
    Итог ВНЕ таблицы: две строки по правому краю. «Без налога (НДС)» вместо
    «В том числе НДС» из эталона — лаборатория работает без НДС, и печатать
    сумму несуществующего налога нельзя.
  -->
  <p class="mt num bold">Итого:&nbsp;&nbsp;${escapeHtml(model.total)}</p>
  <p class="num bold">Без налога (НДС)</p>

  <!--
    Сумма выделена, как и в счёте: это единственное число в строке, ради
    которого её читают. В счёте так было с самого начала, в акте — нет, и
    одна и та же по смыслу строка выглядела в двух документах по-разному.
  -->
  <p class="mt">Всего оказано услуг ${model.itemCount}, на сумму <span class="bold">${escapeHtml(model.total)} руб.</span></p>
  <p class="bold">${escapeHtml(model.totalInWordsAkt)}</p>

  <p class="mt rule-below">${escapeHtml(model.disclaimer)}</p>

  <table class="signatures plain">
    <tbody>
      <tr>
        <td>
          <p class="bold">ИСПОЛНИТЕЛЬ</p>
          <p>${escapeHtml(model.performerRole)}</p>
          <p class="sign-line sign-block">______________________________________________${facsimileImages(model)}</p>
          <p class="center">${escapeHtml(model.performerSigner)}</p>
        </td>
        <td>
          <p class="bold">ЗАКАЗЧИК</p>
          <p>${escapeHtml(model.customerShort)}</p>
          <p class="sign-line">______________________________________________</p>
          <p class="center">&nbsp;</p>
        </td>
      </tr>
    </tbody>
  </table>`;
}
/** Только тело документа, без обвязки страницы — для встраивания в превью. */
export function renderDocumentBody(model) {
    return model.kind === 'schet' ? schetBody(model) : aktBody(model);
}
/**
 * Класс обёртки предпросмотра внутри приложения.
 *
 * Живёт здесь, а не в разметке экрана: им ограничены стили документа, и если
 * имя разойдётся между стилями и разметкой, предпросмотр останется вовсе
 * без оформления — а понять это по виду трудно, документ просто станет
 * «немного не таким».
 */
export const DOCUMENT_PREVIEW_CLASS = 'otc-doc-preview';
/**
 * Стили документа для предпросмотра внутри приложения.
 *
 * Ограничены обёрткой и дополнены одним правилом, которого нет у печатной
 * страницы: поля листа обнуляются. Рамка предпросмотра рисует свои отступы,
 * а поля листа в 16 мм с каждой стороны съедали бы на телефоне треть ширины.
 *
 * Разрешать перенос внутри слов здесь НЕ надо: лист показывается в своей
 * натуральной ширине и целиком уменьшается, а не сжимается. Пока такое
 * правило стояло, «Поставщик (Исполнитель):» разрывалось на «Исполнител»
 * и «ь» — колонка получала право стать уже слова и пользовалась им.
 */
export function documentPreviewStyles() {
    const root = `.${DOCUMENT_PREVIEW_CLASS}`;
    return `${documentCss(root)}
  ${root} .doc, ${root} .doc-akt { padding: 0; }
`;
}
/** Стили документа для самостоятельной страницы: PDF и скачивание. */
export function documentStyles() {
    return DOCUMENT_CSS;
}
/**
 * Самодостаточная HTML-страница: для скачивания, печати и генерации PDF.
 *
 * `copies` — сколько экземпляров положить в документ, каждый с новой
 * страницы. Нужно печати акта: акт подписывают в двух экземплярах — один
 * клинике, один лаборатории, — а поставить «Копий: 2» в окне печати
 * браузера сайт не может, там всегда единица. PDF по-прежнему один
 * экземпляр: его отправляют письмом, и второй лист там был бы дублем.
 */
export function renderDocumentPage(model, options = {}) {
    const copies = Math.max(1, Math.floor(options.copies ?? 1));
    const лист = `<div class="${model.kind === 'akt' ? 'doc-akt' : 'doc'}">
${renderDocumentBody(model)}
</div>`;
    /* Каждый следующий экземпляр — с новой страницы, а не впритык к предыдущему. */
    const экземпляры = Array.from({ length: copies }, (_, i) => i === 0 ? лист : `<div style="break-before: page; page-break-before: always;">${лист}</div>`).join('\n');
    return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="utf-8">
<title>${escapeHtml(model.title)}</title>
<style>${DOCUMENT_CSS}</style>
</head>
<body>
${экземпляры}
</body>
</html>`;
}
//# sourceMappingURL=html.js.map